import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Buildings, Check, WhatsappLogo, PaperPlaneTilt, Sun, Moon } from "@phosphor-icons/react";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [savingCo, setSavingCo] = useState(false);
  const [savingMe, setSavingMe] = useState(false);
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    try {
      const [c, me, log] = await Promise.all([
        api.get("/company"),
        api.get("/auth/me"),
        api.get("/notifications/log").catch(() => ({ data: [] })),
      ]);
      setCompany(c.data);
      setName(c.data.name);
      setPhone(me.data.phone || "");
      setOptIn(!!me.data.whatsapp_opt_in);
      setTestTo(me.data.phone || "");
      setLogs(log.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveCompany = async (e) => {
    e.preventDefault();
    setSavingCo(true);
    try {
      const { data } = await api.patch("/company", { name });
      setCompany(data);
      toast.success("Company updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingCo(false); }
  };

  const saveMe = async (e) => {
    e.preventDefault();
    setSavingMe(true);
    try {
      await api.patch(`/users/${user.id}`, { phone, whatsapp_opt_in: optIn });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingMe(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a phone number in E.164 format (e.g. +14155551234)"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/test-whatsapp", { to: testTo });
      if (data.ok) {
        toast.success("Test WhatsApp sent! Check your phone.");
      } else {
        toast.error("Send failed: " + (data.error || "unknown"));
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  const sendDigest = async (when) => {
    try {
      const { data } = await api.post("/notifications/send-digest", { when });
      toast.success(`${when === "morning" ? "🌅 Morning" : "🌙 Evening"} digest queued for ${data.recipients} recipients`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!company) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="px-4 md:px-10 py-8 max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Administration</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Settings</h1>
      </div>

      {/* Company */}
      <form onSubmit={saveCompany} className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="company-form">
        <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
          <div className="w-12 h-12 bg-zinc-950 grid place-items-center rounded-sm">
            <Buildings weight="bold" size={22} className="text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl text-zinc-950">{company.name}</div>
            <div className="text-xs font-mono text-zinc-500">{company.slug}</div>
          </div>
          <div className="ml-auto">
            <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
              {company.plan || "free"} plan
            </span>
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Company name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="lom-input" data-testid="company-name-input" />
        </div>
        <div className="flex justify-end pt-2 border-t border-zinc-100">
          <button type="submit" disabled={savingCo} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm disabled:opacity-50" data-testid="company-save-btn">
            <Check size={14} weight="bold" />
            {savingCo ? "Saving…" : "Save company"}
          </button>
        </div>
      </form>

      {/* My contact + WhatsApp */}
      <form onSubmit={saveMe} className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="my-profile-form">
        <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
          <div className="w-12 h-12 bg-emerald-500 grid place-items-center rounded-sm">
            <WhatsappLogo weight="bold" size={22} className="text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl text-zinc-950">WhatsApp notifications</div>
            <div className="text-xs text-zinc-500">Receive alerts for late arrivals, missed clock-outs, corrections and daily digests.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Your WhatsApp number (E.164)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="lom-input font-mono" placeholder="+14155551234" data-testid="my-phone-input" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} data-testid="my-optin-checkbox" />
              Opt in to WhatsApp notifications
            </label>
          </div>
        </div>
        <div className="flex justify-end pt-2 border-t border-zinc-100">
          <button type="submit" disabled={savingMe} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm disabled:opacity-50" data-testid="my-save-btn">
            <Check size={14} weight="bold" />
            {savingMe ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>

      {/* Test WhatsApp */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="pb-4 border-b border-zinc-100">
          <div className="font-heading font-bold text-lg text-zinc-950">Send a test message</div>
          <div className="text-xs text-zinc-500 mt-1">
            Requires TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN in <span className="font-mono">backend/.env</span> and a phone that has joined your Twilio WhatsApp sandbox.
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="label-eyebrow block mb-2">To phone (E.164)</label>
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="lom-input font-mono" placeholder="+14155551234" data-testid="test-to-input" />
          </div>
          <button
            onClick={sendTest}
            disabled={sending}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-sm disabled:opacity-50"
            data-testid="send-test-whatsapp-btn"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            Send test
          </button>
        </div>
      </div>

      {/* Manager digest */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="pb-4 border-b border-zinc-100">
          <div className="font-heading font-bold text-lg text-zinc-950">Manager daily digest</div>
          <div className="text-xs text-zinc-500 mt-1">
            Send an at-a-glance WhatsApp to all opted-in admins & supervisors. Recommended cron: 07:00 and 19:00 in your store timezone.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => sendDigest("morning")} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm" data-testid="send-morning-digest-btn">
            <Sun size={14} weight="bold" /> Send morning roster
          </button>
          <button onClick={() => sendDigest("evening")} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm" data-testid="send-evening-digest-btn">
            <Moon size={14} weight="bold" /> Send evening wrap-up
          </button>
        </div>
      </div>

      {/* Manager digest */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="pb-4 border-b border-zinc-100">
          <div className="font-heading font-bold text-lg text-zinc-950">Manager daily digest</div>
          <div className="text-xs text-zinc-500 mt-1">
            Send an at-a-glance WhatsApp to all opted-in admins & supervisors. Recommended cron: 07:00 and 19:00 in your store timezone.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => sendDigest("morning")} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm" data-testid="send-morning-digest-btn">
            <Sun size={14} weight="bold" /> Send morning roster
          </button>
          <button onClick={() => sendDigest("evening")} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm" data-testid="send-evening-digest-btn">
            <Moon size={14} weight="bold" /> Send evening wrap-up
          </button>
        </div>
      </div>

      {/* Notification log */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">Recent</div>
            <div className="font-heading font-bold text-lg text-zinc-950">Notification log</div>
          </div>
          <div className="text-xs font-mono text-zinc-500">{logs.length} entries</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>When</Th><Th>To</Th><Th>Kind</Th><Th>Status</Th><Th>Body</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {logs.length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-8">No messages sent yet</td></tr>
            )}
            {logs.map((r) => (
              <tr key={r.id} data-testid={`notif-row-${r.id}`}>
                <Td className="font-mono text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}</Td>
                <Td className="font-mono text-xs">{r.to}</Td>
                <Td><span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded-sm">{r.kind}</span></Td>
                <Td>
                  {r.ok
                    ? <span className="text-emerald-700 font-bold text-xs">SENT</span>
                    : <span className="text-red-700 font-bold text-xs">FAILED</span>}
                  {r.error && <div className="text-xs text-red-600 mt-0.5 max-w-xs truncate">{r.error}</div>}
                </Td>
                <Td className="text-xs text-zinc-600 max-w-md truncate">{r.body}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 align-top ${className}`}>{children}</td>
);
